// V2 app chrome — the prototype's `.topbar`. Light DOM so studio.css `.topbar`
// /.brandmark/.chip/.icon-btn/.avatar apply directly. Brandmark (Ricoh R + Decks
// + RDx badge), nav chips, theme toggle. Matches prototype app.jsx NavBar.

import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { navigate, currentPath, subscribe } from '../lib/router-state';
import { signOutFirebase } from '../lib/firebase';

// Folio mark — an abstract "stacked deck / aperture" glyph. Three offset sheets
// (the deck) read at a slight angle: two recessed back sheets in a muted ink,
// the front sheet in full ink with a hollow aperture window — evoking layered
// pages + a slide frame without spelling a letter. `currentColor` carries the
// ink so the mark adapts to dark/light chrome (set to --text-primary in CSS);
// the front-sheet accent edge picks up --brand-primary for a restrained spark.
const FOLIO_MARK = html`<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <!-- recessed back sheets (the stack) -->
  <rect x="7.5" y="3.5" width="13" height="13" rx="3" fill="currentColor" opacity="0.18"/>
  <rect x="5.5" y="5.5" width="13" height="13" rx="3" fill="currentColor" opacity="0.34"/>
  <!-- front sheet -->
  <rect x="3.5" y="7.5" width="13" height="13" rx="3" fill="currentColor"/>
  <!-- aperture window punched through the front sheet -->
  <rect x="7.4" y="11.4" width="5.2" height="5.2" rx="1.4" fill="var(--surface-base, #fff)"/>
  <!-- restrained accent: the front sheet's leading edge -->
  <path d="M6.5 7.5 h7 a3 3 0 0 1 3 3 v0" stroke="var(--brand-primary, currentColor)" stroke-width="1.6" stroke-linecap="round" fill="none"/>
</svg>`;
const MOON = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`;
const SUN = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>`;
const SIGN_OUT = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>`;

const TABS: { label: string; href: string }[] = [
  { label: 'Home', href: '/' },
  { label: 'Decks', href: '/library' },
  { label: 'Templates', href: '/templates' },
  { label: 'Shared with me', href: '/shared' },
  { label: 'Gallery', href: '/gallery' },
  { label: 'Images', href: '/images' },
  { label: 'Eval', href: '/eval' },
];

@customElement('app-topbar')
export class AppTopbar extends LitElement {
  @state() private path = currentPath();
  @state() private dark = document.documentElement.getAttribute('data-theme') === 'workplace-slate'
    || document.documentElement.getAttribute('data-theme') === 'dark';
  private unsub?: () => void;

  protected createRenderRoot(): HTMLElement { return this; }

  connectedCallback(): void {
    super.connectedCallback();
    this.unsub = subscribe((p) => { this.path = p; });
  }
  disconnectedCallback(): void { super.disconnectedCallback(); this.unsub?.(); }

  private active(href: string): boolean {
    if (href === '/') return this.path === '/';
    if (href === '/library') return this.path.startsWith('/library') || this.path.startsWith('/decks');
    return this.path.startsWith(href);
  }

  private toggleTheme(): void {
    this.dark = !this.dark;
    const theme = this.dark ? 'workplace-slate' : 'workplace-paper';
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('rdx-decks-theme', theme); } catch { /* ignore */ }
  }

  private async signOut(): Promise<void> {
    try { await signOutFirebase(); } catch { /* tear down session regardless */ }
    navigate('/login');
  }

  render(): TemplateResult {
    return html`
      <div class="topbar">
        <div class="brandmark">
          <span class="mark">${FOLIO_MARK}</span>
          <span class="product">Folio</span>
          <span class="badge badge--brand" style="margin-left:2px">RDx</span>
          <span class="sep"></span>
        </div>
        <nav class="row gap-1" style="display:flex;gap:4px">
          ${TABS.map((t) => html`
            <button class="chip ${this.active(t.href) ? 'is-active' : ''}" @click=${() => navigate(t.href)}>${t.label}</button>`)}
        </nav>
        <div class="spacer"></div>
        <button class="icon-btn" title="Toggle light / dark" aria-label="Toggle theme" @click=${() => this.toggleTheme()}>
          ${this.dark ? SUN : MOON}
        </button>
        <div class="avatar">RDx</div>
        <button class="icon-btn" title="Sign out" aria-label="Sign out" @click=${() => this.signOut()}>
          ${SIGN_OUT}
        </button>
      </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'app-topbar': AppTopbar }
}
